'use client';

import { CopyAll, Visibility } from '@mui/icons-material';
import { IconButton, InputAdornment, Stack, type SxProps, TextField } from '@mui/material';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { setClipboard } from '@campaign-manager/core/lib/clipboard';

import Button from '../Button';
import Dialog from '../Dialog';

interface Props {
  value?: string;
  helperText?: ReactNode | string | undefined;
  name?: string;
  variant?: 'filled' | 'outlined' | 'standard' | undefined;
  revealDialogTitle?: string;
  sx?: SxProps;
}

export default function SensitiveValueField({
  value: initValue,
  helperText,
  revealDialogTitle,
  variant = 'filled',
  sx,
}: Props) {
  const [value, setValue] = useState(initValue);
  const [opaqueValue] = useState('************************************');

  const [showRevealDialog, setShowRevealDialog] = useState(false);

  useEffect(() => {
    setValue(initValue);
  }, [initValue]);

  const handleCopyValueClick = useCallback(() => {
    setClipboard(value || '');
  }, [value]);

  const handleRevealValueClick = useCallback(() => {
    setShowRevealDialog(true);
  }, []);

  return (
    <>
      <TextField
        value={opaqueValue}
        variant={variant}
        autoComplete="off"
        helperText={helperText}
        disabled
        sx={sx}
        slotProps={{
          input: {
            endAdornment: (
              <Stack
                direction="row"
                sx={{
                  gap: 3,
                }}
              >
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleRevealValueClick}
                    edge="end"
                    size="small"
                    aria-label="Reveal value"
                  >
                    <Visibility fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    onClick={handleCopyValueClick}
                    edge="end"
                    size="small"
                    aria-label="Copy value"
                  >
                    <CopyAll fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              </Stack>
            ),
          },
        }}
      />
      <Dialog
        open={showRevealDialog}
        onClose={() => setShowRevealDialog(false)}
        title={revealDialogTitle}
        actions={
          <Stack
            direction="row"
            sx={{
              gap: 2,
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <Button
              type="button"
              color="inherit"
              onClick={() => setShowRevealDialog(false)}
            >
              Close
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              onClick={() => {
                handleCopyValueClick();
                setShowRevealDialog(false);
              }}
            >
              Copy
            </Button>
          </Stack>
        }
      >
        <TextField
          value={value}
          variant="filled"
          autoComplete="off"
          helperText={helperText}
          disabled
          multiline
          rows={10}
          sx={{ width: '100%' }}
        />
      </Dialog>
    </>
  );
}
